import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";

const SessionTimeoutWarning = ({
  show,
  onExtend,
  timeRemaining,
  stage = "warning", // "warning", "critical", "expired"
  onLogout,
}) => {
  const [countdown, setCountdown] = useState(timeRemaining || 0);
  const [isExtending, setIsExtending] = useState(false); // 🔧 NEW: Add loading state
  const { timeoutMinutes } = useAuth();
  // Update countdown when timeRemaining prop changes
  useEffect(() => {
    setCountdown(timeRemaining || 0);
  }, [timeRemaining]);

  // Live countdown timer
  useEffect(() => {
    if (!show || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [show, countdown]);

  // 🔧 ENHANCED: Handle Continue Working with better error handling and loading state
  const handleContinueWorking = useCallback(async () => {
    if (isExtending) return; // Prevent double-clicks

    setIsExtending(true);

    try {
      const result = await onExtend();

      // If the session extension was successful, the modal should close automatically
      // If it failed, the modal might stay open depending on the error type
      if (!result?.success) {
        console.log(
          "Session extension failed, modal remains open for user retry"
        );
        // The extendSession function in AuthContext will handle showing appropriate error messages
        // We don't need to show additional errors here
      }
    } catch (error) {
      console.error("Error in continue working handler:", error);
      // Don't show additional error here since onExtend (extendSession) should handle it
    } finally {
      setIsExtending(false);
    }
  }, [onExtend, isExtending]);

  if (!show) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  // Handle logout - use passed function or fallback to window redirect
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      console.error("⚠️ No logout handler provided to SessionTimeoutWarning");
    }
  };

  // Configure appearance based on stage
  const getStageConfig = () => {
    switch (stage) {
      case "critical":
        return {
          headerClass: "bg-danger text-white",
          alertClass: "alert-danger",
          icon: "bi-exclamation-triangle-fill",
          title: "⚠️ Session Timeout - Final Warning!",
          message: "Your session is about to expire!",
          showExtend: true,
          backdropOpacity: "0.8",
        };
      case "expired":
        return {
          headerClass: "bg-dark text-white",
          alertClass: "alert-dark",
          icon: "bi-lock-fill",
          title: "🔒 Session Expired",
          message:
            "For your security, you have been logged out due to inactivity.",
          showExtend: false,
          backdropOpacity: "0.9",
        };
      default: // "warning"
        return {
          headerClass: "bg-warning text-dark",
          alertClass: "alert-warning",
          icon: "bi-clock-history",
          title: "⏰ Session Timeout Warning",
          message: "Your session will expire soon due to inactivity.",
          showExtend: true,
          backdropOpacity: "0.6",
        };
    }
  };

  const config = getStageConfig();

  return (
    <div
      className="modal show d-block"
      style={{
        backgroundColor: `rgba(0,0,0,${config.backdropOpacity})`,
        backdropFilter: stage === "expired" ? "blur(3px)" : "none",
        zIndex: 9999,
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className={`modal-header ${config.headerClass} border-0`}>
            <h4 className="modal-title fw-bold">
              <i className={`bi ${config.icon} me-2`}></i>
              {config.title}
            </h4>
            {stage !== "expired" && (
              <button
                type="button"
                className={`btn-close ${
                  stage === "critical" ? "btn-close-white" : ""
                }`}
                onClick={handleLogout}
                aria-label="Close"
              ></button>
            )}
          </div>

          <div className="modal-body p-4">
            {stage === "expired" ? (
              // Session Expired Content
              <div className="text-center">
                <div className="mb-4">
                  <i className="bi bi-shield-lock display-1 text-muted"></i>
                </div>
                <h5 className="mb-3">{config.message}</h5>
                <p className="text-muted mb-4">
                  This is required for HIPAA compliance and to protect sensitive
                  consumer information. Please log in again to continue working.
                </p>
                <div className="alert alert-info border-0">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    All unsaved work may be lost. Please remember to save your
                    work regularly.
                  </small>
                </div>
              </div>
            ) : (
              // Warning/Critical Content
              <>
                <div className={`alert ${config.alertClass} border-0 mb-4`}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    <strong>{config.message}</strong>
                  </div>
                </div>

                {countdown > 0 && (
                  <div className="text-center mb-4">
                    <div
                      className={`display-4 fw-bold ${
                        stage === "critical" ? "text-danger" : "text-warning"
                      }`}
                      style={{ fontFamily: "monospace" }}
                    >
                      {minutes}:{seconds.toString().padStart(2, "0")}
                    </div>
                    <p className="text-muted mt-2">
                      {stage === "critical"
                        ? "Seconds remaining"
                        : "Time remaining"}
                    </p>
                  </div>
                )}

                <div className="bg-light p-3 rounded mb-4">
                  <h6 className="text-muted mb-2">
                    <i className="bi bi-shield-check me-2"></i>
                    HIPAA Security Notice
                  </h6>
                  <p className="small text-muted mb-2">
                    For consumers privacy protection, sessions automatically
                    expire after {timeoutMinutes} minutes of inactivity. Please
                    save any work and log out when finished.
                  </p>
                  <p className="small text-muted mb-0">
                    <strong>Important:</strong> Never leave PHI visible on your
                    screen when away from your workstation.
                  </p>
                </div>

                {stage === "critical" && (
                  <div className="alert alert-danger border-0 mb-3">
                    <small>
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      <strong>Final Warning:</strong> Save your work
                      immediately!
                    </small>
                  </div>
                )}

                {/* 🔧 NEW: Show helpful message during extension */}
                {isExtending && (
                  <div className="alert alert-info border-0 mb-3">
                    <small>
                      <i className="bi bi-hourglass-split me-1"></i>
                      <strong>Extending session...</strong> Please wait.
                    </small>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer border-0 p-4 pt-0">
            {stage === "expired" ? (
              // Expired: Only login option
              <div className="d-grid w-100">
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Log In Again
                </button>
              </div>
            ) : (
              // Warning/Critical: Show both options
              <div className="d-flex gap-2 w-100">
                {config.showExtend && onExtend && (
                  <button
                    type="button"
                    className={`btn flex-fill ${
                      stage === "critical" ? "btn-success" : "btn-primary"
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleContinueWorking();
                    }}
                    disabled={isExtending} // 🔧 Prevent double-clicks
                  >
                    {isExtending ? (
                      <>
                        <i className="bi bi-hourglass-split me-2"></i>
                        Extending Session...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Continue Working
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary flex-fill"
                  onClick={handleLogout}
                  disabled={isExtending} // 🔧 Also disable logout during extension
                >
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Log Out Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
